export const SITE_ORIGIN = "https://www.autonxt.in";

// 1. Person Schema Generator (Generic & Connected)
export const getPersonSchema = (name: string, jobTitle: string, image?: string, sameAs?: string[]) => {
  const personSlug = name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9\-]/g, "");
  return {
    "@type": "Person",
    "@id": `${SITE_ORIGIN}/#person/${personSlug}`,
    "name": name,
    "jobTitle": jobTitle,
    ...(image ? { "image": image.startsWith("http") ? image : `${SITE_ORIGIN}${image.startsWith("/") ? image : `/${image}`}` } : {}),
    ...(sameAs && sameAs.length > 0 ? { "sameAs": sameAs } : {}),
    "worksFor": {
      "@id": `${SITE_ORIGIN}/#organization`
    },
    "affiliation": {
      "@id": `${SITE_ORIGIN}/#organization`
    }
  };
};

// 2. Organization Schema (Canonical representation used across the site)
export const getOrgSchema = () => ({
  "@type": "Organization",
  "@id": `${SITE_ORIGIN}/#organization`,
  "name": "AutoNXT Automation",
  "alternateName": ["AutoNxt", "AutoNxt Automation", "AutoNxt Electric Tractors"],
  "legalName": "AutoNxt Automation Pvt. Ltd.",
  "url": SITE_ORIGIN,
  "logo": {
    "@type": "ImageObject",
    "@id": `${SITE_ORIGIN}/#logo`,
    "url": `${SITE_ORIGIN}/small-logo-white-sm.webp`,
    "caption": "AutoNXT Automation Logo",
    "width": 80,
    "height": 73
  },
  "image": {
    "@id": `${SITE_ORIGIN}/#logo`
  },
  "description": "AutoNXT is India's leading electric tractor pioneer, developing commercial-ready autonomous electric tractors with smart technology (V2G, Smart VCU) and high-torque PMSM motors.",
  "email": "sales@autonxt.in",
  "telephone": "+91-9067404606",
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+91-9067404606",
    "contactType": "sales",
    "email": "sales@autonxt.in",
    "areaServed": "IN",
    "availableLanguage": ["en", "hi", "mr", "te"]
  },
  "foundingDate": "2016",
  "founder": [
    { "@id": `${SITE_ORIGIN}/#person/kaustubh-dhonde` },
    { "@id": `${SITE_ORIGIN}/#person/pankaj-goyal` }
  ],
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "704 & 705, Amfotech IT Park, Rd 8, Wagle Estate Rd, Padwal Nagar, Thane West",
    "addressLocality": "Thane",
    "addressRegion": "Maharashtra",
    "postalCode": "400604",
    "addressCountry": "IN"
  },
  "sameAs": [
    "https://www.linkedin.com/company/autonxt-automation",
    "https://www.youtube.com/@autonxtautomation8368",
    "https://www.instagram.com/autonxt_automation/",
    "https://x.com/auto_nxt",
    "https://www.facebook.com/autonxtautomation/"
  ],
  "knowsAbout": [
    "Industrial Electric Vehicles",
    "Autonomous Industrial Vehicles",
    "Industrial Mobility Solutions",
    "Electric Tractors",
    "Autonomous Electric Tractors",
    "Agricultural Automation",
    "Precision Agriculture",
    "Smart Farming",
    "Agricultural Robotics",
    "Industrial Haulage",
    "Material Handling Vehicles",
    "Electric Powertrain Systems",
    "Vehicle Control Units (VCU)",
    "Battery Management Systems (BMS)",
    "Permanent Magnet Synchronous Motors (PMSM)",
    "Vehicle-to-Grid (V2G)",
    "Artificial Intelligence in Mobility",
    "Autonomous Navigation",
    "Connected Vehicle Technology",
    "Fleet Management Systems",
    "Sustainable Mobility",
    "Zero-Emission Transportation",
    "Clean Energy Vehicles",
    "Off-Highway Vehicles",
    "Heavy-Duty Electric Vehicles",
    "Construction Equipment",
    "Mining Equipment",
    "Warehouse Automation",
    "Logistics Automation",
    "Port Automation",
    "Airport Ground Support Vehicles",
    "Smart Industrial Automation"
  ],
  "brand": {
    "@type": "Brand",
    "@id": `${SITE_ORIGIN}/#brand`,
    "name": "AutoNXT"
  }
});

// 3. WebSite Schema (Canonical representation)
export const getWebSiteSchema = () => ({
  "@type": "WebSite",
  "@id": `${SITE_ORIGIN}/#website`,
  "name": "AutoNXT Automation",
  "url": SITE_ORIGIN,
  "publisher": {
    "@id": `${SITE_ORIGIN}/#organization`
  }
});

// 4. AutomotiveBusiness Schema (LocalBusiness representation)
export const getLocalBusinessSchema = () => ({
  "@type": "AutomotiveBusiness",
  "@id": `${SITE_ORIGIN}/#localbusiness`,
  "parentOrganization": {
    "@id": `${SITE_ORIGIN}/#organization`
  },
  "name": "AutoNXT Automation",
  "description": "AutoNXT develops autonomous electric tractors and smart agricultural mobility solutions.",
  "image": `${SITE_ORIGIN}/small-logo-white-sm.webp`,
  "telephone": "+91-9067404606",
  "email": "sales@autonxt.in",
  "url": SITE_ORIGIN,
  "hasMap": "https://www.google.com/maps/search/?api=1&query=704+%26+705+Amfotech+IT+Park,+Thane,+Maharashtra",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "704 & 705, Amfotech IT Park, Rd 8, Wagle Estate Rd, Padwal Nagar, Thane West",
    "addressLocality": "Thane",
    "addressRegion": "Maharashtra",
    "postalCode": "400604",
    "addressCountry": "IN"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 19.1983,
    "longitude": 72.9554
  },
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday"
    ],
    "opens": "10:30",
    "closes": "18:30"
  }
});

// 5. Dynamic WebPage / AboutPage / ContactPage / CollectionPage Schema
export const getWebPageSchema = (
  relativePath: string,
  name: string,
  description: string,
  type: "WebPage" | "AboutPage" | "ContactPage" | "CollectionPage" = "WebPage",
  mainEntityId?: string,
  lang: string = "en",
  image?: string,
  dateModified?: string
) => {
  const cleanPath = relativePath.startsWith("http")
    ? relativePath
    : (relativePath === "/" || relativePath === ""
      ? "/"
      : `${relativePath.startsWith("/") ? relativePath : `/${relativePath}`}${relativePath.endsWith("/") ? "" : "/"}`);
  const url = relativePath.startsWith("http")
    ? relativePath
    : `${SITE_ORIGIN}${cleanPath}`;
  const fullImage = image
    ? (image.startsWith("http") ? image : `${SITE_ORIGIN}${image.startsWith("/") ? image : `/${image}`}`)
    : undefined;

  const isoDate = dateModified || new Date().toISOString().split("T")[0];

  return {
    "@type": type,
    "@id": `${url}#webpage`,
    "url": url,
    "name": name,
    "description": description,
    "isPartOf": {
      "@id": `${SITE_ORIGIN}/#website`
    },
    "publisher": {
      "@id": `${SITE_ORIGIN}/#organization`
    },
    "inLanguage": `${lang}-IN`,
    "dateModified": isoDate,
    ...(fullImage ? {
      "primaryImageOfPage": {
        "@type": "ImageObject",
        "@id": `${url}#primaryimage`,
        "url": fullImage
      },
      "image": {
        "@id": `${url}#primaryimage`
      }
    } : {}),
    ...(relativePath !== "/" && relativePath !== "" ? { "breadcrumb": { "@id": `${url}#breadcrumb` } } : {}),
    ...(mainEntityId ? { "mainEntity": { "@id": mainEntityId } } : {})
  };
};

// 6. BreadcrumbList Schema
export interface BreadcrumbLink {
  name: string;
  path: string;
}

export const getBreadcrumbSchema = (relativePath: string, crumbs: BreadcrumbLink[]) => {
  const cleanPath = relativePath.startsWith("http")
    ? relativePath
    : (relativePath === "/" || relativePath === ""
      ? "/"
      : `${relativePath.startsWith("/") ? relativePath : `/${relativePath}`}${relativePath.endsWith("/") ? "" : "/"}`);
  const url = relativePath.startsWith("http")
    ? relativePath
    : `${SITE_ORIGIN}${cleanPath}`;

  const itemListElement = crumbs.map((crumb, idx) => {
    const crumbCleanPath = crumb.path.startsWith("http")
      ? crumb.path
      : (crumb.path === "/" || crumb.path === ""
        ? "/"
        : `${crumb.path.startsWith("/") ? crumb.path : `/${crumb.path}`}${crumb.path.endsWith("/") ? "" : "/"}`);
    const crumbUrl = crumb.path.startsWith("http")
      ? crumb.path
      : `${SITE_ORIGIN}${crumbCleanPath}`;
    return {
      "@type": "ListItem",
      "position": idx + 1,
      "name": crumb.name,
      "item": crumbUrl
    };
  });

  return {
    "@type": "BreadcrumbList",
    "@id": `${url}#breadcrumb`,
    "itemListElement": itemListElement
  };
};

// 7. FAQPage Schema
export const getFAQSchema = (faqs: Array<{ q: string; a: string }>, lang: string = "en") => ({
  "@type": "FAQPage",
  "inLanguage": `${lang}-IN`,
  "mainEntity": (faqs || []).map((faq) => ({
    "@type": "Question",
    "name": faq.q,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.a
    }
  }))
});

// 8. Product Schema
export interface ProductSpec {
  label: string;
  value: string;
}

export const getProductSchema = (
  slug: string,
  name: string,
  desc: string,
  image: string | Array<Record<string, any>> | string[],
  specs: ProductSpec[],
  sku?: string,
  category = "Electric Tractor",
  model?: string,
  lang: string = "en"
) => {
  const url = `${SITE_ORIGIN}/product/${slug}`;
  let finalImage: any = image;
  if (typeof image === "string") {
    finalImage = image.startsWith("http")
      ? image
      : `${SITE_ORIGIN}${image.startsWith("/") ? image : `/${image}`}`;
  } else if (Array.isArray(image)) {
    finalImage = image.map(img => {
      if (typeof img === "string") {
        return img.startsWith("http") ? img : `${SITE_ORIGIN}${img.startsWith("/") ? img : `/${img}`}`;
      }
      return img;
    });
  }

  // Extract model code from slug/display name if not explicitly provided
  const finalModel = model || (slug ? slug.split("/").pop()?.toUpperCase() : undefined);

  return {
    "@type": "Product",
    "@id": `${url}#product`,
    "name": name,
    "image": finalImage,
    "description": desc,
    "url": url,
    "inLanguage": `${lang}-IN`,
    "brand": {
      "@id": `${SITE_ORIGIN}/#brand`
    },
    "sku": sku || `AUTONXT-${finalModel || "TRACTOR"}`,
    "mpn": sku || `AUTONXT-${finalModel || "TRACTOR"}`,
    "model": finalModel,
    "category": category,
    "manufacturer": {
      "@id": `${SITE_ORIGIN}/#organization`
    },
    "offers": {
      "@type": "Offer",
      "@id": `${url}#offer`,
      "url": `${SITE_ORIGIN}/book/`,
      "priceCurrency": "INR",
      "price": "0.00",
      "priceValidUntil": "2027-12-31",
      "itemCondition": "https://schema.org/NewCondition",
      "availability": "https://schema.org/InStock",
      "seller": {
        "@id": `${SITE_ORIGIN}/#organization`
      }
    },
    "additionalProperty": (specs || []).map((spec) => ({
      "@type": "PropertyValue",
      "name": spec.label,
      "value": spec.value
    }))
  };
};

const getSlugFromName = (name: string): string => {
  const slug = name.toLowerCase().trim().replace(/\s+/g, "-");
  const cleanSlug = slug.replace(/[^a-z0-9\-]/g, "");
  // Fallback to a deterministic hash if string is entirely non-ASCII / non-alphanumeric
  if (cleanSlug.replace(/-/g, "") === "") {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      const char = name.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return `author-${Math.abs(hash)}`;
  }
  return cleanSlug;
};

// 9. Article Schema (for news / blogs)
export const getArticleSchema = (
  pageRelativePath: string,
  type: "BlogPosting" | "NewsArticle",
  headline: string,
  summary: string,
  date: string,
  image: string,
  articleSlug: string,
  authorName?: string,
  externalUrl?: string,
  lang: string = "en"
) => {
  const url = externalUrl || `${SITE_ORIGIN}${pageRelativePath.startsWith("/") ? pageRelativePath : `/${pageRelativePath}`}`;
  const fullImage = image
    ? (image.startsWith("http") ? image : `${SITE_ORIGIN}${image.startsWith("/") ? image : `/${image}`}`)
    : `${SITE_ORIGIN}/small-logo-white-sm.webp`;

  const authorSlug = authorName
    ? getSlugFromName(authorName)
    : "autonxt-team";

  return {
    "@type": type,
    "@id": `${url}#article/${articleSlug}`,
    "headline": headline,
    "description": summary,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${url}#webpage`
    },
    "author": authorName
      ? {
        "@type": "Person",
        "@id": `${SITE_ORIGIN}/#person/${authorSlug}`,
        "name": authorName
      }
      : {
        "@id": `${SITE_ORIGIN}/#organization`
      },
    "datePublished": date,
    "dateModified": date,
    "image": fullImage,
    "publisher": {
      "@id": `${SITE_ORIGIN}/#organization`
    },
    "inLanguage": `${lang}-IN`,
    "url": url
  };
};

// 10. VideoObject Schema (for product showcases)
export const getVideoObjectSchema = (
  pageUrl: string,
  name: string,
  desc: string,
  thumbnailUrl: string,
  uploadDate: string,
  contentUrl: string,
  videoSlug: string,
  embedUrl?: string,
  duration?: string
) => ({
  "@type": "VideoObject",
  "@id": `${pageUrl}#video/${videoSlug}`,
  "name": name,
  "description": desc,
  "thumbnailUrl": thumbnailUrl.startsWith("http") ? thumbnailUrl : `${SITE_ORIGIN}${thumbnailUrl.startsWith("/") ? thumbnailUrl : `/${thumbnailUrl}`}`,
  "uploadDate": uploadDate,
  "contentUrl": contentUrl,
  "embedUrl": embedUrl || contentUrl,
  "duration": duration || undefined,
  "publisher": {
    "@id": `${SITE_ORIGIN}/#organization`
  }
});

